import { api } from './api';

// GT Pontok — a /api/loyalty/* végpontok kliens-oldali szerződése.
// A szabályokat és címleteket a backend adja (nem hardkódoljuk itt).

export interface LoyaltyCoupon {
  points: number;
  valueGross: number;
  available: boolean;
}

export interface LoyaltyHistoryItem {
  id: number;
  at: string;
  label: string;
  delta: number;
  balanceAfter: number;
}

export interface LoyaltyEarnRule {
  key: string;
  label: string;
  detail?: string;
  points?: number;
}

export interface LoyaltyTier {
  name: string;
  threshold: number;
}

export interface LoyaltyReferral {
  /** null: még nincs ajánló-kód (POST /loyalty/referral-code hozza létre). */
  code: string | null;
  url: string | null;
  count: number;
  points: number;
}

export interface LoyaltyPhoto {
  id: number;
  /** Nyilvános (jóváhagyott) kép URL-je. */
  url: string | null;
  /** Nem nyilvános (jóváhagyásra váró / elutasított) kép hitelesített útvonala. */
  privatePath: string | null;
  caption: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

export type MyCouponStatus = 'active' | 'used' | 'expired' | 'void';

/** A vevő GT Pont beváltásából kapott kupon („Kuponjaim"). */
export interface MyLoyaltyCoupon {
  code: string;
  valueGross: number;
  minOrderValue: number;
  validUntil: string | null;
  createdAt: string;
  status: MyCouponStatus;
}

export interface LoyaltyMe {
  balance: number;
  lifetimePoints: number;
  tier: LoyaltyTier | null;
  nextTier: LoyaltyTier | null;
  coupons: LoyaltyCoupon[];
  history: LoyaltyHistoryItem[];
  historyHasMore: boolean;
  myCoupons: MyLoyaltyCoupon[];
  earnRules: LoyaltyEarnRule[];
  referral: LoyaltyReferral;
  photos: LoyaltyPhoto[];
  photoPoints: number;
  couponValidDays: number;
}

export interface RedeemResult {
  couponCode: string;
  valueGross: number;
  balance: number;
  validUntil: string | null;
}

export interface LoyaltyHistoryPage {
  results: LoyaltyHistoryItem[];
  hasMore: boolean;
}

export interface ReferralCodeResult {
  code: string;
  url: string;
}

export const getLoyaltyMe = () =>
  api.get<LoyaltyMe>('/loyalty/me').then((r) => r.data);

export const getLoyaltyHistory = (offset: number, limit = 20) =>
  api.get<LoyaltyHistoryPage>('/loyalty/history', { params: { offset, limit } }).then((r) => r.data);

export const redeemPoints = (points: number) =>
  api.post<RedeemResult>('/loyalty/redeem', { points }).then((r) => r.data);

export const createReferralCode = () =>
  api.post<ReferralCodeResult>('/loyalty/referral-code').then((r) => r.data);

export const uploadLoyaltyPhoto = (file: File, caption = '') => {
  const fd = new FormData();
  fd.append('image', file);
  if (caption) fd.append('caption', caption);
  // A közös api JSON-alapértelmezését töröljük, hogy a böngésző multipart boundary-t rakjon.
  return api
    .post<LoyaltyPhoto>('/loyalty/photos', fd, { headers: { 'Content-Type': null } })
    .then((r) => r.data);
};

// --- Admin (operátori) --------------------------------------------------------

export interface AdminLoyaltyRow {
  user_id: number;
  account_id: number | null;
  email: string;
  name: string;
  is_staff: boolean;
  balance: number;
  lifetime_points: number;
  referral_code: string;
  updated_at: string | null;
}

export interface PageMeta {
  count: number;
  page: number;
  page_size: number;
}

export interface AdminLoyaltyPage extends PageMeta {
  results: AdminLoyaltyRow[];
}

export interface AdminLoyaltyTx {
  id: number;
  delta: number;
  reason: string;
  reason_display: string;
  label: string;
  balance_after: number;
  created_at: string;
  created_by: string | null;
}

export interface AdminLoyaltyDetail extends AdminLoyaltyRow {
  transactions: AdminLoyaltyTx[];
  transactions_meta: PageMeta;
  can_adjust: boolean;
  adjust_limit: number;
}

export interface AdminLoyaltyStats {
  outstandingPoints: number;
  accountsWithBalance: number;
  liabilityMinHuf: number;
  liabilityMaxHuf: number;
  activeCoupons: number;
  activeCouponsValueHuf: number;
  expiredCoupons: number;
  expiredCouponsValueHuf: number;
}

export type PhotoStatus = LoyaltyPhoto['status'];
export type PhotoAction = 'approve' | 'reject' | 'revoke';

export interface AdminLoyaltyPhoto extends LoyaltyPhoto {
  user_id: number;
  user_email: string;
  user_name: string;
  approvedAt: string | null;
}

export interface AdminLoyaltyPhotoPage extends PageMeta {
  results: AdminLoyaltyPhoto[];
}

export const adminLoyaltyApi = {
  list: (params: { q?: string; page: number; page_size: number }) =>
    api.get<AdminLoyaltyPage>('/admin/loyalty/accounts/', { params }).then((r) => r.data),
  stats: () => api.get<AdminLoyaltyStats>('/admin/loyalty/stats/').then((r) => r.data),
  detail: (userId: number, page = 1) =>
    api.get<AdminLoyaltyDetail>(`/admin/loyalty/users/${userId}/`, { params: { page } }).then((r) => r.data),
  adjust: (userId: number, delta: number, label: string) =>
    api.post<AdminLoyaltyRow>(`/admin/loyalty/users/${userId}/adjust/`, { delta, label }).then((r) => r.data),
  photos: (params: { status?: PhotoStatus; page: number; page_size: number }) =>
    api.get<AdminLoyaltyPhotoPage>('/admin/loyalty/photos/', { params }).then((r) => r.data),
  photoAction: (id: number, action: PhotoAction) =>
    api.post<AdminLoyaltyPhoto>(`/admin/loyalty/photos/${id}/${action}/`).then((r) => r.data),
};
